import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useArcPlc } from "api-toolkit/useArcPlc";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import {
  computeFarmPaymentTotals,
  isFarmPaymentCategory,
  REVENUE_LABEL_MISCELLANEOUS_PROGRAM_PAYMENTS,
  REVENUE_LABEL_OTHER_GOVERNMENT_PAYMENTS,
} from "components/ArcPlc/FarmPaymentConstants";
import { BUDGET_STATUS_ACTIVE } from "components/Budget/helpers";
import { claimPaymentAmount } from "components/Insurance/InsuranceService";
import { MODULE_ARC_PLC, MODULE_INSURANCE } from "components/Menu/config/constants";
import { formatNumber, formatPrice, formatProducerName } from "utility/formatters";

import { resolveUnsoldUnitPrice } from "components/Budget/projectedPrice";
import { SALE_CATEGORY_BASIS, SALE_CATEGORY_CASH, SALE_CATEGORY_HTA, SALE_CATEGORY_NO_PRICE } from "./SalesHelper";

export const REVENUE_LABEL_ARC_PLC_PAYMENTS = "ARC/PLC Payments (projected)";

export const useRevenueReport = ({ onError, currentYear, producerState }) => {
  const { globalState: contextGlobalState } = useContext(AppContext);
  // Prefer producerState if provided, otherwise use context globalState
  const globalState = producerState || contextGlobalState;
  const { producer_token } = globalState || {};

  // State for loading status
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Data states
  const [transactionHistory, setTransactionHistory] = useState(null);
  const [salesByCategory, setSalesByCategory] = useState(null);
  const [positionSummaryData, setPositionSummaryData] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState({});
  const [insurancePlanClaims, setInsurancePlanClaims] = useState([]);
  const [farmPayments, setFarmPayments] = useState([]);
  const [isLoadingFarmPayments, setIsLoadingFarmPayments] = useState(false);
  const [cropProfiles, setCropProfiles] = useState([]);

  // ARC/PLC related states
  const [baseAcres, setBaseAcres] = useState([]);
  const [isLoadingArcPlc, setIsLoadingArcPlc] = useState(false);
  const [arcCoCountyYieldOverrides, setArcCoCountyYieldOverrides] = useState([]);

  // Use ARC/PLC hook for calculations
  // Pass globalState so it uses the correct producer_token (from URL params or impersonation)
  const arcPlc = useArcPlc({
    baseAcres,
    apiConfig: producerState,
    crop_year: currentYear,
    arcCoCountyYieldOverrides,
  });

  const getArcPlcByElectionFnRef = useRef(arcPlc.actions.getArcPlcByElection);
  getArcPlcByElectionFnRef.current = arcPlc.actions.getArcPlcByElection;

  // Helper functions
  const parseValue = useCallback(
    (val) => (typeof val === "string" ? parseFloat(val.replace(/[^0-9.-]+/g, "")) || 0 : Number(val) || 0),
    []
  );

  const getBudgetCountyPractices = useCallback((budget) => {
    return budget?.budgetcountypractice ?? budget?.BudgetCountyPractice ?? [];
  }, []);

  const normalizeLineItems = useCallback((lineItems) => {
    if (!lineItems) return [];
    return Array.isArray(lineItems) ? lineItems : [lineItems];
  }, []);

  const calculateProductionCost = useCallback(
    (item) => {
      if (!item) return 0;

      const acreage = Number(item.reported_acreage) || 0;

      // ExportObjectGraph nests rolled-up cost on li[] (production_cost is practice total, not per acre)
      if (Array.isArray(item.li) && item.li.length) {
        const practiceTotalCost = item.li.reduce((sum, entry) => sum + (Number(entry.production_cost) || 0), 0);
        if (practiceTotalCost > 0 && acreage > 0) {
          return practiceTotalCost / acreage;
        }

        const nestedLineItems = item.li.flatMap((entry) => normalizeLineItems(entry.BudgetCountyPracticeLineItem));
        if (nestedLineItems.length) {
          return nestedLineItems.reduce((sum, line) => sum + (Number(line.value) || 0), 0);
        }
      }

      const directLineItems = normalizeLineItems(item.BudgetCountyPracticeLineItem);
      if (directLineItems.length) {
        return directLineItems.reduce((sum, line) => sum + (Number(line.value) || 0), 0);
      }

      if (item.production_cost_per_acre) {
        return Number(item.production_cost_per_acre) || 0;
      }
      if (item.production_cost && acreage > 0) {
        return Number(item.production_cost) / acreage;
      }

      return 0;
    },
    [normalizeLineItems]
  );

  // Data loading functions
  const loadTransactionHistory = useCallback(async () => {
    if (!currentYear || !producer_token) return;

    setIsLoadingData(true);
    try {
      const filter = {
        year: currentYear,
        producer_token,
        is_posted: true,
      };
      const result = await UsersApiHelper.users_search("UserTransactionHistory", filter, {});
      const sorted = result.sort((a, b) => new Date(a.trade_date) - new Date(b.trade_date));
      setTransactionHistory(sorted);
    } catch (err) {
      onError(err);
    } finally {
      setIsLoadingData(false);
    }
  }, [currentYear, producer_token, onError]);

  const loadSalesByCategory = useCallback(async () => {
    if (!currentYear || !producer_token) return;

    try {
      const filter = { year: currentYear, producer_token };
      const data = await UsersApiHelper.users_search("PositionSummarySalesByCategory", filter, {});
      setSalesByCategory(data);
    } catch (err) {
      setSalesByCategory(null);
      onError(err);
    }
  }, [currentYear, producer_token, onError]);

  const loadPositionSummaryData = useCallback(async () => {
    if (!currentYear || !producer_token) return;

    setIsLoadingData(true);
    try {
      const filter = { year: currentYear, producer_token };
      const data = await UsersApiHelper.users_search("PositionSummary", filter, {});
      setPositionSummaryData(data);
    } catch (err) {
      setPositionSummaryData(null);
      onError(err);
    } finally {
      setIsLoadingData(false);
    }
  }, [currentYear, producer_token, onError]);

  const loadCropProfiles = useCallback(async () => {
    if (!currentYear || !producer_token) return;
    try {
      const data = await UsersApiHelper.users_search("CropProfile", { is_active: true, producer_token }, {});
      setCropProfiles(data || []);
    } catch (err) {
      onError(err);
    }
  }, [currentYear, producer_token, onError]);

  const budgetSummaryReport = useCallback(async () => {
    if (!producer_token || !currentYear) return;

    try {
      const filter = {
        producer_token,
        is_active: true,
        year: currentYear,
        status: BUDGET_STATUS_ACTIVE,
      };
      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null,
      });
      const dataDictionary = await api.callPost(
        "data/Users/Budget/BudgetSummaryReport/ExportObjectGraph",
        filter,
        globalState
      );
      setBudgets(dataDictionary || {});
    } catch (err) {
      onError(err);
    }
  }, [producer_token, currentYear, globalState, onError]);

  // ARC/PLC data loading functions
  const loadBaseAcres = useCallback(async () => {
    if (!currentYear || !producer_token) return;

    try {
      setIsLoadingArcPlc(true);
      const baseAcresFilter = {
        crop_year: currentYear,
        producer_token,
        is_active: true,
      };
      const baseAcresData = await UsersApiHelper.selectProducerFarmBaseAcres(baseAcresFilter, globalState);
      setBaseAcres(baseAcresData || []);

      try {
        const api = new ApiHelper({
          baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
          config: {},
          sessionManager: _sessionManager,
          _instance: null,
        });
        const overrides = await api.callPost("ArcCoCountyYieldOverride/search", baseAcresFilter, globalState);
        setArcCoCountyYieldOverrides(Array.isArray(overrides) ? overrides : []);
      } catch (err) {
        setArcCoCountyYieldOverrides([]);
        onError(err);
      }
    } catch (err) {
      setBaseAcres([]);
      setArcCoCountyYieldOverrides([]);
      onError(err);
    } finally {
      setIsLoadingArcPlc(false);
    }
  }, [currentYear, producer_token, globalState, onError]);

  const loadInsurancePlanClaims = useCallback(async () => {
    if (!currentYear || !producer_token) return;

    try {
      const filter = {
        producer_token,
        is_active: true,
        reinsurance_year: currentYear,
        module: MODULE_INSURANCE,
      };
      const data = await UsersApiHelper.users_search("InsurancePlanClaim", filter, globalState);
      setInsurancePlanClaims(data || []);
    } catch (err) {
      setInsurancePlanClaims([]);
      onError(err);
    }
  }, [currentYear, producer_token, globalState, onError]);

  const loadFarmPayments = useCallback(async () => {
    if (!currentYear || !producer_token) return;

    setIsLoadingFarmPayments(true);
    try {
      const filter = {
        producer_token,
        reinsurance_year: currentYear,
        module: MODULE_ARC_PLC,
        is_active: true,
      };
      const data = await UsersApiHelper.users_search("InsurancePlanClaim", filter, globalState);
      setFarmPayments((data || []).filter((payment) => isFarmPaymentCategory(payment.payment_category)));
    } catch (err) {
      setFarmPayments([]);
      onError(err);
    } finally {
      setIsLoadingFarmPayments(false);
    }
  }, [currentYear, producer_token, globalState, onError]);

  const farmPaymentTotals = useMemo(() => computeFarmPaymentTotals(farmPayments), [farmPayments]);

  const getFarmPaymentTotalsForCommodity = useCallback(
    (commodityName) => computeFarmPaymentTotals(farmPayments, commodityName),
    [farmPayments]
  );

  const [arcPlcElections, setArcPlcElections] = useState(null);
  const arcOverridesSerialized = JSON.stringify(arcCoCountyYieldOverrides ?? []);
  const baseAcresStableKey = JSON.stringify(baseAcres ?? []);

  useEffect(() => {
    setArcPlcElections(null);
  }, [currentYear, baseAcresStableKey]);

  const loadArcPlcElections = useCallback(async () => {
    if (!currentYear || !producer_token) return;

    const baseAcresCropYear = baseAcres?.[0]?.crop_year;
    if (baseAcres?.length && String(baseAcresCropYear) !== String(currentYear)) {
      return;
    }

    // Check if all required dependencies are loaded
    if (!baseAcres?.length || !arcPlc.programElections?.length) {
      console.warn("loadArcPlcElections - dependencies not ready:", {
        baseAcres: baseAcres?.length,
        programElections: arcPlc.programElections?.length,
        isLoadingProgramElections: arcPlc.isLoadingProgramElections,
      });
      return;
    }

    const data = await getArcPlcByElectionFnRef.current();
    setArcPlcElections(data);
  }, [
    currentYear,
    producer_token,
    baseAcresStableKey,
    arcPlc.programElections?.length,
    arcPlc.isLoadingProgramElections,
    arcOverridesSerialized,
  ]);

  useEffect(() => {
    const baseAcresCropYear = baseAcres?.[0]?.crop_year;
    const baseAcresMatchYear = !baseAcres?.length || String(baseAcresCropYear) === String(currentYear);

    if (
      currentYear &&
      producer_token &&
      baseAcresMatchYear &&
      baseAcres?.length &&
      arcPlc.programElections?.length &&
      !arcPlc.isLoadingProgramElections
    ) {
      loadArcPlcElections();
    }
  }, [
    currentYear,
    producer_token,
    baseAcresStableKey,
    arcPlc.programElections?.length,
    arcPlc.isLoadingProgramElections,
    loadArcPlcElections,
    arcOverridesSerialized,
  ]);

  // Calculate ARC/PLC payments for a specific crop (now synchronous using cached values)
  const getArcPlcPaymentForCrop = useCallback(
    (commodityName) => {
      if (!commodityName || !arcPlcElections) {
        // console.log("arcPlcElections is not loaded");
        return 0;
      }

      // Handle case where arcPlcElections is an object (expected)
      if (typeof arcPlcElections === "object" && !Array.isArray(arcPlcElections)) {
        return arcPlcElections[commodityName?.toLowerCase()] || 0;
      }

      // Handle case where arcPlcElections is unexpectedly an array
      if (Array.isArray(arcPlcElections)) {
        const match = arcPlcElections.find(
          (item) =>
            item?.commodityName?.toLowerCase() === commodityName?.toLowerCase() ||
            item?.commodity_name?.toLowerCase() === commodityName?.toLowerCase()
        );

        return match?.payment || match?.value || 0;
      }

      return 0;
    },
    [arcPlcElections]
  );

  // Calculate total crop insurance payments revenue
  const calculateCropInsurancePayments_Revenue = useMemo(() => {
    if (!insurancePlanClaims?.length) return 0;

    return insurancePlanClaims.reduce((total, claim) => {
      return total + claimPaymentAmount(claim);
    }, 0);
  }, [insurancePlanClaims]);

  // Calculate crop insurance payments for a specific commodity
  const getCropInsurancePaymentForCrop = useCallback(
    (commodityName) => {
      if (!insurancePlanClaims?.length || !commodityName) return 0;

      return insurancePlanClaims
        .filter(
          (claim) =>
            claim?.commodity_name?.toLowerCase?.().trim?.() === commodityName?.toLowerCase?.().trim?.()
        )
        .reduce((total, claim) => {
          return total + claimPaymentAmount(claim);
        }, 0);
    },
    [insurancePlanClaims]
  );

  // Load all data in parallel
  useEffect(() => {
    if (currentYear && producer_token) {
      setIsLoadingData(true);
      const loadAllData = async () => {
        try {
          await Promise.all([
            budgetSummaryReport(),
            loadSalesByCategory(),
            loadPositionSummaryData(),
            loadTransactionHistory(),
            loadBaseAcres(),
            loadInsurancePlanClaims(),
            loadFarmPayments(),
            loadCropProfiles(),
          ]);
        } finally {
          setIsLoadingData(false);
        }
      };
      loadAllData();
    }
  }, [currentYear, producer_token]); // Removed unnecessary dependencies

  // Transaction history helper functions
  const getTransactionHistoryRowsByCommodity = useCallback(
    (commodity_name) => {
      const rows =
        transactionHistory?.filter(
          (s) => s.crop_profile_commodity_name === commodity_name && !s.offset_match_id && s.is_active
        ) || [];
      return rows;
    },
    [transactionHistory]
  );

  const calculateFuturesOptionsHedgedTotal = useCallback(
    (commodity_name) => {
      if (!commodity_name) {
        return 0;
      }

      const transactions = getTransactionHistoryRowsByCommodity(commodity_name);
      if (transactions?.length) {
        return transactions.reduce((acc, curr) => {
          return acc + curr.hedge_quantity * curr.multiplication_factor;
        }, 0);
      } else {
        return 0;
      }
    },
    [getTransactionHistoryRowsByCommodity, transactionHistory]
  );

  const getTransactionHistoryTotal_Revenue = useCallback(
    (commodity_name) => {
      if (!commodity_name) {
        return 0;
      }
      const total = getTransactionHistoryRowsByCommodity(commodity_name)
        ?.filter((x) => !x.is_otc)
        .reduce((total, row) => {
          const mainValue = row.is_open ? row.market_value_open || 0 : row.market_value_closed || 0;
          const matchedValues = getTransactionHistoryRowsByMatch(row.id).reduce(
            (matchTotal, matchRow) => matchTotal + (matchRow.market_value_closed || 0),
            0
          );

          return total + mainValue + matchedValues;
        }, 0);
      return total;
    },
    [transactionHistory]
  );

  const getTransactionHistoryRowsByMatch = useCallback(
    (id) => {
      if (transactionHistory?.length) {
        return transactionHistory.filter((s) => s.offset_match_id == id);
      } else {
        return [];
      }
    },
    [transactionHistory]
  );

  // ---

  // Process budgets into grouped data
  const processCropDetails = useCallback(
    (budgets) => {
      const practices = budgets?.length ? getBudgetCountyPractices(budgets[0]) : [];
      if (!practices.length) return { crops: [] };

      const groupData = (data) => {
        return data.reduce((grouped, item) => {
          const { commodity_name, practice_name, reported_acreage, yield: yieldPerAcre } = item;
          if (!grouped[commodity_name]) grouped[commodity_name] = {};
          if (!grouped[commodity_name][practice_name]) {
            grouped[commodity_name][practice_name] = { totalAcres: 0, totalCost: 0, totalYield: 0 };
          }
          const costPerAcre = calculateProductionCost(item);
          grouped[commodity_name][practice_name].totalAcres += Math.round(reported_acreage);
          grouped[commodity_name][practice_name].totalCost += costPerAcre * reported_acreage;
          grouped[commodity_name][practice_name].totalYield += yieldPerAcre * reported_acreage;
          return grouped;
        }, {});
      };

      const buildCommodityRows = (commodityData) => {
        let totalAcresAll = 0,
          totalCostAll = 0,
          totalYieldAll = 0;
        const rows = Object.keys(commodityData).map((practiceName) => {
          const { totalAcres, totalCost, totalYield } = commodityData[practiceName];
          const costPerAcre = totalAcres ? totalCost / totalAcres : 0;
          const yieldPerAcre = totalAcres ? totalYield / totalAcres : 0;
          totalAcresAll += totalAcres;
          totalCostAll += totalCost;
          totalYieldAll += totalYield;
          return {
            practiceName,
            acres: Math.round(totalAcres),
            costPerAcre: costPerAcre,
            totalCost: totalCost,
            projectedYieldPerAcre: yieldPerAcre,
            totalYield: totalYield,
            breakEven: yieldPerAcre ? totalCost / (yieldPerAcre * totalAcres) : "0",
          };
        });
        // Append total row
        const totalCostPerAcre = totalAcresAll ? totalCostAll / totalAcresAll : 0;
        const totalYieldPerAcre = totalAcresAll ? totalYieldAll / totalAcresAll : 0;
        rows.push({
          practiceName: "Total",
          acres: Math.round(totalAcresAll),
          costPerAcre: totalCostPerAcre,
          totalCost: totalCostAll,
          projectedYieldPerAcre: totalYieldPerAcre,
          totalYield: totalYieldAll,
          breakEven: totalYieldPerAcre ? totalCostAll / totalYieldAll : "0",
          isTotal: true,
        });
        return rows;
      };

      const grouped = groupData(practices);
      const crops = Object.keys(grouped).map((commodity) => ({
        name: commodity,
        rows: buildCommodityRows(grouped[commodity]),
      }));
      return { crops };
    },
    [calculateProductionCost, getBudgetCountyPractices]
  );

  // Process budgets into grouped data when budgets change
  useEffect(() => {
    const processedData = processCropDetails(budgets);
    setRevenueSummary(processedData);
  }, [budgets, processCropDetails]);

  const totalProductionCost = useMemo(() => {
    const practices = budgets?.length ? getBudgetCountyPractices(budgets[0]) : [];
    if (!practices.length) {
      return 0;
    }

    return practices.reduce((sum, item) => {
      const acreage = Number(item.reported_acreage) || 0;
      if (item.production_cost != null) {
        return sum + Number(item.production_cost) || 0;
      }
      return sum + calculateProductionCost(item) * acreage;
    }, 0);
  }, [budgets, calculateProductionCost, getBudgetCountyPractices]);

  // Production-weighted budget projected_price by commodity — fallback when no futures board
  const budgetProjectedPriceByCommodity = useMemo(() => {
    const practices = budgets?.length ? getBudgetCountyPractices(budgets[0]) : [];
    const acc = {};
    for (const p of practices) {
      const name = p.commodity_name;
      if (!name) continue;
      if (!acc[name]) acc[name] = { production: 0, revenue: 0 };
      const production = (Number(p.reported_acreage) || 0) * (Number(p.yield) || 0);
      acc[name].production += production;
      acc[name].revenue += production * (Number(p.projected_price) || 0);
    }
    const out = {};
    for (const [name, { production, revenue }] of Object.entries(acc)) {
      out[name] = production > 0 ? revenue / production : 0;
    }
    return out;
  }, [budgets, getBudgetCountyPractices]);

  // Generate marketing summary
  const generateMarketingSummary = useCallback(
    (data) => {
      if (!data?.length) return null;

      const farmerInfo = { name: formatProducerName(data[0] || "N/A") };
      const crops = data.map((item) => {
        const production = item.production || 0;

        // Calculate sold quantity only from Cash and HTA sales
        const soldCashUnits = item.salesByCategory?.find((sc) => sc.category === SALE_CATEGORY_CASH)?.amount || 0;
        const soldHtaUnits = item.salesByCategory?.find((sc) => sc.category === SALE_CATEGORY_HTA)?.amount || 0;
        const soldBasisUnits = item.salesByCategory?.find((sc) => sc.category === SALE_CATEGORY_BASIS)?.amount || 0;
        const soldOtcUnits = item.salesByCategory?.find((sc) => sc.category === SALE_CATEGORY_NO_PRICE)?.amount || 0;
        const soldNoPriceEstablishedUnits =
          item.salesByCategory?.find((sc) => sc.category === SALE_CATEGORY_NO_PRICE)?.amount || 0;

        // Forward Sales
        const sold = soldCashUnits + soldHtaUnits;

        // Calculate sold Revenue only from Cash and HTA sales
        const soldRevenue =
          item.salesByCategory?.reduce((sum, sc) => {
            if (sc.category === SALE_CATEGORY_CASH || sc.category === SALE_CATEGORY_HTA) {
              return sum + (sc.revenue || 0);
            }
            return sum;
          }, 0) || 0;

        // Futures Options
        const futuresOptionsUnits = calculateFuturesOptionsHedgedTotal(item.commodity_name);

        // Calculate unsold as production minus sold (excluding Basis and NPE)
        const unsold = Math.max(0, production - sold);
        const unitQuantityHedge = item.unit_quantity_hedge_total || 0;
        const unitQuantityOtc = item.unit_quantity_otc || 0;
        const protectedBushels = unitQuantityHedge;
        const soldPercent = production ? ((sold / production) * 100).toFixed(2) + "%" : "0%";
        const unsoldPercent = production ? ((unsold / production) * 100).toFixed(2) + "%" : "0%";
        const hedgedPercent = production ? ((unitQuantityHedge / production) * 100).toFixed(2) + "%" : "0%";
        const protectedPercent = production ? ((protectedBushels / production) * 100).toFixed(2) + "%" : "0%";
        const otcPercent = production ? ((unitQuantityOtc / production) * 100).toFixed(2) + "%" : "0%";
        const production_guarantee = item.production_guarantee || 0;

        // Revenue Forecast — board + basis when Quotes exist; else budget projected_price
        // (no-board crops like Sweet Corn have market_last_price NULL)
        const cpBasis = Number(cropProfiles?.find(
          (cp) =>
            cp.commodity_name?.trim().toLowerCase() === item.commodity_name?.trim().toLowerCase() &&
            String(cp.year) === String(currentYear)
        )?.basis) || 0;
        const priceUnprotected = resolveUnsoldUnitPrice(
          item.market_last_price,
          cpBasis,
          budgetProjectedPriceByCommodity[item.commodity_name]
        );

        // Calculate crop insurance guarantee value (units * price)
        const cropInsuranceGuaranteeValue = production_guarantee * priceUnprotected;

        // Calculate unsold revenue including Basis, NPE, and remaining unsold bushels
        const unsoldRevenue =
          item.salesByCategory?.reduce((sum, sc) => {
            if (sc.category === SALE_CATEGORY_BASIS) {
              return sum + (sc.amount * sc.price_per_unit || priceUnprotected || 0);
            } else if (sc.category === SALE_CATEGORY_NO_PRICE) {
              return sum + (sc.amount * priceUnprotected || 0);
            }
            return sum;
          }, 0) || 0;

        // Calculate remaining unsold bushels (those not in any sale category).
        // Oversold crops (sales exceed projected production, e.g. a stale/bad budget
        // yield) must not subtract phantom revenue.
        const remainingUnsold = Math.max(
          0,
          production - (item.salesByCategory?.reduce((sum, sc) => sum + (sc.amount || 0), 0) || 0)
        );

        const totalUnsoldRevenue = unsoldRevenue + remainingUnsold * priceUnprotected;

        const totalGainLoss = getTransactionHistoryTotal_Revenue(item.commodity_name);
        const openGainLoss = getTransactionHistoryRowsByCommodity(item.commodity_name).reduce((total, row) => {
          return total + (row.market_value_open || 0);
        }, 0);
        const closedGainLoss = totalGainLoss - openGainLoss;

        // Calculate ARC/PLC payments using actual calculations
        const arcPlcPayments = getArcPlcPaymentForCrop(item.commodity_name);

        const cropFarmPaymentTotals = computeFarmPaymentTotals(farmPayments, item.commodity_name);
        const otherGovernmentPayments = cropFarmPaymentTotals.otherGovernment;
        const miscellaneousPayments = cropFarmPaymentTotals.miscellaneous;

        const totalRevenue =
          soldRevenue +
          totalUnsoldRevenue +
          closedGainLoss +
          openGainLoss +
          arcPlcPayments +
          otherGovernmentPayments +
          miscellaneousPayments;

        // Marketing Data
        const cost = totalProductionCost;
        const revenueBooked = soldRevenue + closedGainLoss + openGainLoss;
        const bushelsUnprotected =
          production -
          (soldCashUnits + soldHtaUnits + soldBasisUnits + soldNoPriceEstablishedUnits + futuresOptionsUnits);
        const breakevenPrice =
          bushelsUnprotected !== 0 ? ((cost - revenueBooked) / bushelsUnprotected).toFixed(2) : "N/A";

        return {
          name: item.commodity_name,
          yieldDataTitle: item.commodity_name,
          yieldData: [
            { label: "Projected Production", value: production, percent: null },
            {
              label: "Sold",
              value: sold,
              percent: soldPercent,
              detailedUnits: {
                cash: soldCashUnits,
                hta: soldHtaUnits,
                basis: soldBasisUnits,
                noPriceEstablished: soldNoPriceEstablishedUnits,
                otc: soldOtcUnits,
                unprotected: bushelsUnprotected,
              },
            },
            { label: "Unsold", value: unsold, percent: unsoldPercent },
            { label: "Total Hedged Position", value: unitQuantityHedge, percent: hedgedPercent },
            { label: `Protected`, value: protectedBushels, percent: protectedPercent },
            { label: "OTC", value: unitQuantityOtc, percent: otcPercent },
          ],
          revenueDataTitle: "Revenue Forecast",
          revenueData: [
            {
              label: "Sold Revenue",
              value: soldRevenue,
              detailedRevenue: {
                amount_cash: item.salesByCategory?.find((sc) => sc.category === SALE_CATEGORY_CASH)?.revenue || 0,
                amount_hta: item.salesByCategory?.find((sc) => sc.category === SALE_CATEGORY_HTA)?.revenue || 0,
                amount_futures_options: calculateFuturesOptionsHedgedTotal(item.commodity_name),
                amount_basis: item.salesByCategory?.find((sc) => sc.category === SALE_CATEGORY_BASIS)?.revenue || 0,
                amount_unprotected: (() => {
                  const totalSold = item.salesByCategory?.reduce((total, sc) => total + (sc.amount || 0), 0) || 0;
                  const futuresOptions = calculateFuturesOptionsHedgedTotal(item.commodity_name);
                  return Math.max(0, (item.production || 0) - totalSold - futuresOptions);
                })(),
              },
            },
            { label: "Unsold Revenue", value: totalUnsoldRevenue },
            { label: "Closed Gain/Loss", value: closedGainLoss },
            { label: "Open Gain/Loss", value: openGainLoss },
            { label: REVENUE_LABEL_ARC_PLC_PAYMENTS, value: arcPlcPayments },
            { label: REVENUE_LABEL_OTHER_GOVERNMENT_PAYMENTS, value: otherGovernmentPayments },
            { label: REVENUE_LABEL_MISCELLANEOUS_PROGRAM_PAYMENTS, value: miscellaneousPayments },
            { label: `Total Revenue ${item.commodity_name}`, value: totalRevenue, isBold: true },
          ],
          marketingDataTitle: "Marketing and Sales",
          marketingData: [
            { label: "Average Sales Price", value: formatPrice(sold ? soldRevenue / sold : 0, 4) },
            { label: "Breakeven", value: formatPrice(cost / production, 4) },
            { label: "Revenue Booked", value: formatPrice(revenueBooked, 0) },
            { label: "Production Cost", value: formatPrice(cost, 0) },
            { label: "Unprotected", value: formatNumber(bushelsUnprotected, 0) },
            { label: "Price to Breakeven on Remaining Bushels", value: formatPrice(Math.max(breakevenPrice, 0), 4) },
          ],
          insuranceDataTitle: "Insurance and Other",
          insuranceData: [{ label: "Crop Insurance Guarantee", value: cropInsuranceGuaranteeValue }],
          boardPrice: item.market_last_price || 0,
          cropBasis: cpBasis,
          cost,
        };
      });

      // Compute the main revenue forecast summary
      const mainRevenueForcast = [
        "Sold Revenue",
        "Unsold Revenue",
        "Closed Gain/Loss",
        "Open Gain/Loss",
        REVENUE_LABEL_ARC_PLC_PAYMENTS,
      ].map((label) => {
        const total = crops.reduce((sum, crop) => {
          const item = crop.revenueData.find((r) => r.label === label);
          return sum + parseValue(item?.value);
        }, 0);
        return { label: label.replace(" Revenue", ""), value: total };
      });
      const projectedRevenue = mainRevenueForcast.reduce((sum, item) => sum + item.value, 0);
      mainRevenueForcast.push({ label: "Projected Revenue", value: projectedRevenue, isBold: true });

      return { crops, mainRevenueForcast, farmerInfo };
    },
    [
      totalProductionCost,
      farmPayments,
      getTransactionHistoryRowsByCommodity,
      getTransactionHistoryTotal_Revenue,
      parseValue,
      getArcPlcPaymentForCrop,
      calculateFuturesOptionsHedgedTotal,
      cropProfiles,
      currentYear,
      budgetProjectedPriceByCommodity,
    ]
  );

  // Combine report data from position summary and sales by category
  const revenueDetail = useMemo(() => {
    if (positionSummaryData?.length) {
      const reportClone = [...positionSummaryData];
      if (salesByCategory?.length) {
        reportClone.forEach((ps) => {
          ps.salesByCategory = salesByCategory.filter((sbc) => sbc.commodity_name === ps.commodity_name);
          if (ps.production) {
            ps.salesByCategory.forEach((sale) => {
              sale.percentage_of_production = sale.amount / ps.production;
            });
          }
        });
      }
      const result = generateMarketingSummary(reportClone);
      return result;
    }
    return null;
  }, [positionSummaryData, salesByCategory, generateMarketingSummary]);

  // Calculate farm planting data
  const farmPlantingData = useMemo(() => {
    if (!revenueSummary?.crops?.length) return null;

    const totalAcres = revenueSummary.crops.reduce((total, crop) => {
      // Only use the total row for each crop to avoid double counting
      const totalRow = crop.rows?.find((row) => row.isTotal);
      return total + (totalRow?.acres || 0);
    }, 0);

    const totalCost = revenueSummary.crops.reduce((total, crop) => {
      // Only use the total row for each crop to avoid double counting
      const totalRow = crop.rows?.find((row) => row.isTotal);
      return total + (totalRow?.totalCost || 0);
    }, 0);

    return {
      totalAcres,
      costPerAcre: totalAcres > 0 ? totalCost / totalAcres : 0,
    };
  }, [revenueSummary]);

  const finalLoadingState = isLoadingData || isLoadingArcPlc || isLoadingFarmPayments;

  return {
    isLoadingData: finalLoadingState,
    revenueDetail,
    revenueSummary,
    calculateFuturesOptionsHedgedTotal,
    farmPlantingData,
    getTransactionHistoryTotal_Revenue,
    getArcPlcPaymentForCrop,
    calculateCropInsurancePayments_Revenue,
    getCropInsurancePaymentForCrop,
    farmPayments,
    farmPaymentTotals,
    isLoadingFarmPayments,
    getFarmPaymentTotalsForCommodity,
  };
};

export default useRevenueReport;
